import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth, ADMIN_SECTIONS_ORDER } from './contexts/AuthContext';
import { ResponseModalProvider } from './contexts/ResponseModalContext';
import Login from './components/Login';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import AdminLayout from './components/admin/AdminLayout';
import Dashboard from './components/admin/Dashboard';
import MinhaConta from './components/admin/MinhaConta';
import Vendedores from './components/admin/Vendedores';
import Metas from './components/admin/Metas';
import GruposMetas from './components/admin/GruposMetas';
import Filtros from './components/admin/Filtros';
import Variaveis from './components/admin/Variaveis';
import Relatorios from './components/admin/Relatorios';
import Vendas from './components/admin/Vendas';
import Monitoramento from './components/admin/Monitoramento';
import BIMetas from './components/admin/BIMetas';
import Modulos from './components/admin/Modulos';
import Usuarios from './components/admin/Usuarios';
import Funcionarios from './components/admin/Funcionarios';
import IA from './components/admin/IA';
import SessionExpiredNotification from './components/SessionExpiredNotification';
import Loading from './components/common/Loading';

function App() {
  return (
    <AuthProvider>
      <ResponseModalProvider>
        <Router basename={process.env.PUBLIC_URL || '/'} future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <div className="App">
            <SessionExpiredNotification />

            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminIndexRoute />} />
                <Route path="vendas" element={<ProtectedSectionRoute section="vendas"><Vendas /></ProtectedSectionRoute>} />
                <Route path="relatorios" element={<ProtectedSectionRoute section="relatorios"><Relatorios /></ProtectedSectionRoute>} />
                <Route path="monitoramento" element={<ProtectedSectionRoute section="monitoramento"><Monitoramento /></ProtectedSectionRoute>} />
                <Route path="bi-metas" element={<ProtectedSectionRoute section="bi_metas"><BIMetas /></ProtectedSectionRoute>} />
                <Route path="vendedores" element={<ProtectedSectionRoute section="vendedores"><Vendedores /></ProtectedSectionRoute>} />
                <Route path="metas" element={<ProtectedSectionRoute section="metas"><Metas /></ProtectedSectionRoute>} />
                <Route path="grupos-metas" element={<ProtectedSectionRoute section="grupos_metas"><GruposMetas /></ProtectedSectionRoute>} />
                <Route path="filtros" element={<ProtectedSectionRoute section="filtros"><Filtros /></ProtectedSectionRoute>} />
                <Route path="variaveis" element={<ProtectedSectionRoute section="variaveis"><Variaveis /></ProtectedSectionRoute>} />
                <Route path="modulos" element={<ProtectedSectionRoute section="modulos"><Modulos /></ProtectedSectionRoute>} />
                <Route path="funcionarios" element={<ProtectedSectionRoute section="funcionarios"><Funcionarios /></ProtectedSectionRoute>} />
                <Route path="usuarios" element={<ProtectedSectionRoute section="usuarios"><Usuarios /></ProtectedSectionRoute>} />
                <Route path="ia" element={<ProtectedSectionRoute section="ia"><IA /></ProtectedSectionRoute>} />
                <Route path="minha-conta" element={<ProtectedSectionRoute section="minha_conta"><MinhaConta /></ProtectedSectionRoute>} />
              </Route>
              <Route path="/" element={<Navigate to="/admin" replace />} />
            </Routes>
          </div>
        </Router>
      </ResponseModalProvider>
    </AuthProvider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Loading
        size="lg"
        text="Carregando aplicação..."
        subtext="Verificando autenticação e preparando ambiente..."
        minHeight="100vh"
      />
    );
  }

  if (!user) {
    const currentPath = location.pathname + location.search + location.hash;
    const redirectParam = (currentPath && currentPath !== '/' && !currentPath.includes('/login'))
      ? `?redirect=${encodeURIComponent(currentPath)}`
      : '';
    return <Navigate to={`/login${redirectParam}`} state={{ from: location }} replace />;
  }

  return children;
}

function AdminIndexRoute() {
  const { hasSectionAccess, getFirstAccessiblePath } = useAuth();

  if (hasSectionAccess('dashboard', 'view')) {
    return <Dashboard />;
  }

  const firstPath = getFirstAccessiblePath ? getFirstAccessiblePath() : '/admin/minha-conta';
  const destination = firstPath === '/admin' ? '/admin/minha-conta' : firstPath;
  return <Navigate to={destination} replace />;
}

function ProtectedSectionRoute({ children, section }) {
  const { hasSectionAccess, user, getFirstAccessiblePath } = useAuth();

  const isAllowed = (section === 'usuarios')
    ? (user?.role === 'admin' && hasSectionAccess('usuarios', 'view'))
    : hasSectionAccess(section, 'view');

  if (!isAllowed) {
    const firstPath = getFirstAccessiblePath ? getFirstAccessiblePath() : '/admin';
    const currentConfig = (ADMIN_SECTIONS_ORDER || []).find((item) => item.section === section);
    if (firstPath === currentConfig?.path) {
      return <Navigate to="/admin/minha-conta" replace />;
    }
    return <Navigate to={firstPath} replace />;
  }

  return children;
}

export default App;
